import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { toast } from "react-toastify";
import {
  listLabMethods,
  createLabMethod,
  updateLabMethod,
  deleteLabMethod,
} from "api/labMasters";
import { getTests } from "api/reportTemplate";
import {
  canEditRecord,
  isGlobalRecord,
  readOnlyReason,
  summariseMappings,
  defaultMappingCount,
  findDuplicateMappingIndex,
  normaliseParameterName,
  WHOLE_TEST_LABEL,
  STATUS_FILTERS,
} from "utils/labMasterUi";

const PER_PAGE_OPTIONS = [10, 20, 50];
const IS_PLATFORM_ADMIN = true;

const emptyMapping = () => ({ test: "", parameterName: "", isDefault: false });

const toFormMappings = (mappings) =>
  (Array.isArray(mappings) ? mappings : []).map((mapping) => ({
    test:
      mapping?.test && typeof mapping.test === "object" ?
        String(mapping.test._id)
      : String(mapping?.test ?? ""),
    parameterName: mapping?.parameterName ?? "",
    isDefault: Boolean(mapping?.isDefault),
  }));

const ScopeChip = ({ record }) => (
  <Chip
    size="small"
    label={isGlobalRecord(record) ? "Global" : "Facility"}
    title={
      isGlobalRecord(record) ?
        "Global — Managed by DRCFO Admin"
      : "Facility — Managed by your Hospital/Clinic"
    }
    sx={{
      height: 22,
      fontSize: 11,
      fontWeight: 700,
      bgcolor: isGlobalRecord(record) ? "#EFF6FF" : "#EEF2FF",
      color: isGlobalRecord(record) ? "#1D4ED8" : "#4338CA",
    }}
  />
);

const StatusChip = ({ active }) => (
  <Chip
    size="small"
    label={active ? "Active" : "Inactive"}
    sx={{
      height: 22,
      fontSize: 11,
      fontWeight: 700,
      bgcolor: active ? "#ECFDF5" : "#F3F4F6",
      color: active ? "#047857" : "#6B7280",
    }}
  />
);

const MethodFormDialog = ({ open, mode, method, tests, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const readOnly = mode === "view";
  const isEdit = mode === "edit";

  const [name, setName] = useState("");
  const [reportDisplayName, setReportDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setServerError("");
    setName(method?.name || "");
    setReportDisplayName(method?.reportDisplayName || "");
    setDescription(method?.description || "");
    setIsActive(method?.isActive !== false);
    setMappings(toFormMappings(method?.mappings));
  }, [open, method]);

  const duplicateIndex = useMemo(
    () => findDuplicateMappingIndex(mappings),
    [mappings],
  );

  const updateMapping = (index, field, value) =>
    setMappings((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const handleSubmit = async () => {
    setSubmitted(true);
    setServerError("");
    if (!name.trim() || !reportDisplayName.trim()) return;
    if (duplicateIndex !== -1) return;
    if (mappings.some((m) => !m.test)) {
      setServerError("Every mapping needs a test. Remove any blank rows.");
      return;
    }

    const payload = {
      name: name.trim(),
      reportDisplayName: reportDisplayName.trim(),
      description: description.trim(),
      isActive,
      mappings: mappings.map((m) => ({
        test: m.test,
        parameterName: normaliseParameterName(m.parameterName),
        isDefault: Boolean(m.isDefault),
      })),
    };

    setSaving(true);
    const res =
      isEdit ?
        await updateLabMethod(method?._id, payload)
      : await createLabMethod(payload);
    setSaving(false);

    if (res.ok) {
      onClose(true, isEdit ? "Method updated" : "Method created");
      return;
    }
    setServerError(res.error || "Something went wrong. Please try again.");
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
          {readOnly ?
            "Method details"
          : isEdit ?
            "Edit method"
          : "Add method"}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
          Global laboratory methods referenced by report template parameters.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#F9FAFB" }}>
        {serverError && (
          <Box
            role="alert"
            sx={{
              mb: 2,
              p: 1.25,
              borderRadius: 2,
              border: "1px solid #FECACA",
              bgcolor: "#FEF2F2",
              color: "#B91C1C",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            {serverError}
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Method Name"
              required
              disabled={readOnly}
              value={name}
              error={submitted && !name.trim()}
              helperText={
                submitted && !name.trim() ?
                  "Method name is required"
                : "e.g. CHOD-POD, GPO Method, ISE"
              }
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Report Display Name"
              required
              disabled={readOnly}
              value={reportDisplayName}
              error={submitted && !reportDisplayName.trim()}
              helperText={
                submitted && !reportDisplayName.trim() ?
                  "Report display name is required"
                : "Printed beneath the parameter name on the report."
              }
              onChange={(e) => setReportDisplayName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Description"
              disabled={readOnly}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  disabled={readOnly}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Test / Parameter mapping
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                Leave the parameter blank to map the whole test (
                {WHOLE_TEST_LABEL}). Only one method can be default per
                test/parameter.
              </Typography>
            </Box>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setMappings((prev) => [...prev, emptyMapping()])}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add mapping
              </Button>
            )}
          </Box>

          {mappings.length === 0 ?
            <Typography
              sx={{
                p: 2,
                textAlign: "center",
                border: "1px dashed #D1D5DB",
                borderRadius: 2,
                fontSize: 12.5,
                color: "#6B7280",
                bgcolor: "#fff",
              }}
            >
              No mappings yet. A method can be created now and mapped later.
            </Typography>
          : mappings.map((mapping, index) => (
              <Grid
                container
                spacing={1}
                key={index}
                alignItems="center"
                sx={{
                  mb: 1,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border:
                    duplicateIndex === index ? "1px solid #FCA5A5" : (
                      "1px solid #E5E7EB"
                    ),
                }}
              >
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small" disabled={readOnly}>
                    <InputLabel id={`map-test-${index}`}>Test *</InputLabel>
                    <Select
                      labelId={`map-test-${index}`}
                      label="Test *"
                      value={mapping.test}
                      onChange={(e) =>
                        updateMapping(index, "test", e.target.value)
                      }
                    >
                      <MenuItem value="">Select a test</MenuItem>
                      {tests.map((test) => (
                        <MenuItem key={test._id} value={test._id}>
                          {test.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Parameter"
                    placeholder={WHOLE_TEST_LABEL}
                    disabled={readOnly}
                    value={mapping.parameterName}
                    error={duplicateIndex === index}
                    helperText={
                      duplicateIndex === index ?
                        "This test/parameter is already mapped"
                      : ""
                    }
                    onChange={(e) =>
                      updateMapping(index, "parameterName", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={8} sm={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(mapping.isDefault)}
                        disabled={readOnly || !isActive}
                        onChange={(e) =>
                          updateMapping(index, "isDefault", e.target.checked)
                        }
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 12.5 }}>Default</Typography>
                    }
                  />
                </Grid>
                <Grid item xs={4} sm={1} sx={{ textAlign: "right" }}>
                  {!readOnly && (
                    <Tooltip title="Remove mapping">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove mapping"
                        onClick={() =>
                          setMappings((prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            ))
          }
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => onClose(false)}
          disabled={saving}
          sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
        >
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, px: 3 }}
          >
            {saving ?
              "Saving…"
            : isEdit ?
              "Save changes"
            : "Create method"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const MethodMasterList = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState("");
  const [tests, setTests] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listLabMethods({
      page: page + 1,
      perPage,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(activeOnly ? { activeOnly } : {}),
    });
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Error while fetching methods!");
      setRows([]);
      return;
    }
    setRows(res.data);
    setMeta(res.meta);
  }, [page, perPage, search, activeOnly]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getTests({ perPage: 1000, withAdmin: true });
        const payload = res?.data;
        setTests(
          Array.isArray(payload?.data) ?
            payload.data
          : payload?.data?.data || [],
        );
      } catch {
        setTests([]);
      }
    })();
  }, []);

  const testNameById = useMemo(
    () =>
      tests.reduce((acc, test) => {
        acc[String(test._id)] = test.name;
        return acc;
      }, {}),
    [tests],
  );

  const toggleActive = async (method) => {
    setBusy(true);
    const res = await updateLabMethod(method._id, {
      isActive: !method.isActive,
    });
    setBusy(false);
    if (res.ok) {
      toast.success(
        method.isActive ? "Method deactivated" : "Method activated",
      );
      load();
    } else {
      toast.error(res.error || "Status update failed!");
    }
  };

  const runDelete = async () => {
    setBusy(true);
    const res = await deleteLabMethod(confirm._id);
    setBusy(false);
    if (res.ok) {
      setConfirm(null);
      toast.success("Method deleted successfully");
      load();
    } else {
      toast.error(res.error || "Delete failed!");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 20 } }}>
            Method Master
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
            Global laboratory methods (CHOD-POD, ISE, Manual…) available to
            every facility.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setModal({ mode: "create", method: null })}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Add Method
        </Button>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={5}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            placeholder="Method or display name"
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="method-status">Status</InputLabel>
            <Select
              labelId="method-status"
              label="Status"
              value={activeOnly}
              onChange={(e) => {
                setPage(0);
                setActiveOnly(e.target.value);
              }}
            >
              {STATUS_FILTERS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box
        sx={{ border: "1px solid #E5E7EB", borderRadius: 2, bgcolor: "#fff" }}
      >
        {loading ?
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        : rows.length === 0 ?
          <Typography sx={{ py: 6, textAlign: "center", color: "#6B7280" }}>
            No methods found.
          </Typography>
        : <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead sx={{ background: "#F0F4FF" }}>
                <TableRow>
                  {[
                    "Method",
                    "Report display",
                    "Scope",
                    "Mapped tests / parameters",
                    "Status",
                    "Actions",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{ fontWeight: 700, fontSize: 12.5 }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((method) => {
                  const editable = canEditRecord(method, {
                    isPlatformAdmin: IS_PLATFORM_ADMIN,
                  });
                  const lockReason = readOnlyReason(method, {
                    isPlatformAdmin: IS_PLATFORM_ADMIN,
                  });
                  const summary = summariseMappings(
                    method.mappings,
                    testNameById,
                  );
                  const defaults = defaultMappingCount(method.mappings);

                  return (
                    <TableRow key={method._id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {method.name}
                      </TableCell>
                      <TableCell>{method.reportDisplayName}</TableCell>
                      <TableCell>
                        <ScopeChip record={method} />
                      </TableCell>
                      <TableCell>
                        {summary.shown.length === 0 ?
                          <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
                            Not mapped
                          </Typography>
                        : <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {summary.shown.map((text) => (
                              <Chip
                                key={text}
                                size="small"
                                label={text}
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            ))}
                            {summary.extra > 0 && (
                              <Chip
                                size="small"
                                label={`+${summary.extra} more`}
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                            {defaults > 0 && (
                              <Chip
                                size="small"
                                label={`${defaults} default${defaults > 1 ? "s" : ""}`}
                                sx={{
                                  height: 20,
                                  fontSize: 11,
                                  bgcolor: "#FEF3C7",
                                  color: "#B45309",
                                }}
                              />
                            )}
                          </Box>
                        }
                      </TableCell>
                      <TableCell>
                        <StatusChip active={method.isActive !== false} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <Tooltip title="View method">
                            <IconButton
                              size="small"
                              aria-label="View method"
                              onClick={() => setModal({ mode: "view", method })}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={editable ? "Edit method" : lockReason}
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Edit method"
                                disabled={!editable}
                                onClick={() =>
                                  setModal({ mode: "edit", method })
                                }
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              editable ?
                                method.isActive ?
                                  "Deactivate"
                                : "Activate"
                              : lockReason
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Toggle status"
                                disabled={!editable || busy}
                                onClick={() => toggleActive(method)}
                              >
                                {method.isActive ?
                                  <ToggleOnIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                : <ToggleOffIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={editable ? "Delete method" : lockReason}
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Delete method"
                                disabled={!editable}
                                onClick={() => setConfirm(method)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        }

        <TablePagination
          component="div"
          count={meta?.total || 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={perPage}
          rowsPerPageOptions={PER_PAGE_OPTIONS}
          onRowsPerPageChange={(e) => {
            setPerPage(Number(e.target.value));
            setPage(0);
          }}
        />
      </Box>

      <MethodFormDialog
        open={Boolean(modal)}
        mode={modal?.mode}
        method={modal?.method}
        tests={tests}
        onClose={(changed, message) => {
          setModal(null);
          if (changed) {
            toast.success(message || "Saved");
            load();
          }
        }}
      />

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>
          Delete this method?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            &quot;{confirm?.name}&quot; will be removed and will no longer be
            selectable in report templates. Templates that already reference it
            keep their saved configuration.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirm(null)}
            disabled={busy}
            sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={runDelete}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {busy ? "Working…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MethodMasterList;
